import React, { useRef } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Database, Download, Upload, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Backup() {
  const { user } = useAuth();
  const fileRef = useRef();

  const exportData = async () => {
    try {
      const types = ["special_case","counseling_case","individual_session","group_session","info_form","parent_contact","class_visit","program","absence","tardy","behavior_contract","program_doc","daily_incident","monthly_report"];
      const bundle = { counselor_id: user.id, exported_at: new Date().toISOString(), records: {} };
      const profile = await api.get("/profile");
      bundle.profile = profile.data;
      for (const t of types) {
        const { data } = await api.get("/records", { params: { type: t } });
        bundle.records[t] = data;
      }
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `backup-${user.username}-${Date.now()}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("تم تصدير البيانات");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const importData = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      let count = 0;
      for (const [type, list] of Object.entries(bundle.records || {})) {
        for (const rec of list) {
          await api.post("/records", { type, data: rec.data });
          count++;
        }
      }
      if (bundle.profile?.data) await api.put("/profile", { data: bundle.profile.data });
      toast.success(`تم استيراد ${count} سجل`);
      e.target.value = "";
    } catch (err) { toast.error(formatErr(err) || "ملف غير صالح"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#3E6A56]/10 text-[#3E6A56] flex items-center justify-center">
          <Database className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>النسخ الاحتياطي</h2>
          <p className="text-sm text-[#4A554E]">تصدير أو استعادة بياناتك بملف JSON</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
          <div className="w-10 h-10 rounded-xl bg-[#3E6A56]/10 text-[#3E6A56] flex items-center justify-center mb-3">
            <Download className="w-5 h-5" />
          </div>
          <div className="font-bold text-lg mb-1">📤 تصدير البيانات</div>
          <p className="text-sm text-[#4A554E] mb-4">احفظ نسخة احتياطية من جميع سجلاتك.</p>
          <Button onClick={exportData} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="backup-export-button">تصدير الآن</Button>
        </div>
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
          <div className="w-10 h-10 rounded-xl bg-[#C97052]/10 text-[#C97052] flex items-center justify-center mb-3">
            <Upload className="w-5 h-5" />
          </div>
          <div className="font-bold text-lg mb-1">📥 استيراد البيانات</div>
          <p className="text-sm text-[#4A554E] mb-4">استعد بياناتك من ملف JSON.</p>
          <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={importData} />
          <Button onClick={() => fileRef.current?.click()} variant="outline" className="rounded-xl border-[#e6e2d8]">اختر ملف</Button>
        </div>
      </div>
    </div>
  );
}
